import React, { useCallback, useContext, useMemo, useState } from 'react';
import type { NewOrderModalProps } from '../order/NewOrderModal';
import NewOrderModal from '../order/NewOrderModal';
import type { SelectCompletedModalProps } from '../order/SelectCompletedModal';
import SelectCompletedModal from '../order/SelectCompletedModal';
import type { EditOrderModalProps } from '../order/EditOrderModal';
import EditOrderModal from '../order/EditOrderModal';
import type { ShipmentModalProps } from '../shipment/ShipmentModal';
import ShipmentModal from '../shipment/ShipmentModal';
import type { OffHireModalProps } from '../offhire/OffHireModal';
import OffHireModal from '../offhire/OffHireModal';
import type { OpenPositionModalProps } from '../openPosition/OpenPositionModal';
import OpenPositionModal from '../openPosition/OpenPositionModal';
import type { NewCoaModalProps } from '../contracts/NewCoaModal';
import NewCoaModal from '../contracts/NewCoaModal';
import type { EditCoaModalProps } from '../contracts/EditCoaModal';
import EditCoaModal from '../contracts/EditCoaModal';
import type { EditSpotModalProps } from '../voyageCharter/EditSpotModal';
import EditSpotModal from '../voyageCharter/EditSpotModal';
import type { TimeData } from '../core/TimeData';
import type { EventTimeline, TimelineFocus } from '../core/Timelines';
import type { CommitScenarioModalProps } from '../CommitScenarioModal';
import CommitScenarioModal from '../CommitScenarioModal';
import type { SingleEventModalProps } from '../single_event/SingleEventModal';
import SingleEventModal from '../single_event/SingleEventModal';
import type { TcTimeDataByVessel } from '../../lib/planningData';
import type { VoyageCharterModalProps } from '../voyageCharter/VoyageCharterModal';
import VoyageCharterModal from '../voyageCharter/VoyageCharterModal';
import type { VesselReservationModalProps } from '../vesselReservation/VesselReservationModal';
import VesselReservationModal from '../vesselReservation/VesselReservationModal';
import type { VoyageAttributeModalProps } from '../voageAttribute/VoyageAttributeModal';
import VoyageAttributeModal from '../voageAttribute/VoyageAttributeModal';
import type { RelatedOrgsQuery } from '../../graphql';
import type { ContractDetailsSchema } from '../voyageCharter/schema';
import type { BatchEditCargoesModalProps } from '../batchEditCargoes/BatchEditCargoesModal';
import BatchEditCargoesModal from '../batchEditCargoes/BatchEditCargoesModal';
import type { SuggestionEstimatorProps } from '../timeline/suggestions/SuggestionEstimator';
import SuggestionEstimator from '../timeline/suggestions/SuggestionEstimator';

// Available modals
const modals = {
  VIEW_ORDER: {
    component: EditOrderModal,
    label: 'order',
  },
  CREATE_ORDER: {
    component: NewOrderModal,
    label: 'order creation',
  },
  VIEW_SHIPMENT: {
    component: ShipmentModal,
    label: 'shipment',
  },
  EDIT_OFFHIRE: {
    component: OffHireModal,
    label: 'offhire edit',
  },
  EDIT_OPEN_POSITION: {
    component: OpenPositionModal,
    label: 'openPosition edit',
  },
  EDIT_SINGLE_EVENT: {
    component: SingleEventModal,
    label: 'single event edit',
  },
  CREATE_COA: {
    component: NewCoaModal,
    label: 'coa creation',
  },
  EDIT_COA: {
    component: EditCoaModal,
    label: 'coa',
  },
  EDIT_SPOT: {
    component: EditSpotModal,
    label: 'spot',
  },
  COMMIT_SCENARIO: {
    component: CommitScenarioModal,
    label: 'commit scenario',
  },
  SELECT_COMPLETED: {
    component: SelectCompletedModal,
    label: 'select completed',
  },
  EDIT_VOYAGE_CHARTER: {
    component: VoyageCharterModal,
    label: 'cargo',
  },
  VESSEL_RESERVATION: {
    component: VesselReservationModal,
    label: 'vessel reservation',
  },
  VOYAGE_ATTRIBUTE: {
    component: VoyageAttributeModal,
    label: 'voyage attribute',
  },
  BATCH_EDIT_CARGOES: {
    component: BatchEditCargoesModal,
    label: 'batch edit cargoes',
  },
  SUGGESTION_ESTIMATOR: {
    component: SuggestionEstimator,
    label: 'suggestion p/l',
  },
};

type CreateOrderProps = Pick<
  NewOrderModalProps,
  'onCreate' | 'duplicateFromOrderId' | 'currentUserOrg'
>;
type ViewOrderProps = Pick<
  EditOrderModalProps,
  | 'orderId'
  | 'onDelete'
  | 'isOrderAssigned'
  | 'isOrderPublished'
  | 'disableDelete'
>;
type ViewShipmentProps = Pick<
  ShipmentModalProps,
  | 'vessel'
  | 'vessels'
  | 'vesselIndex'
  | 'orders'
  | 'tcTimeDataByVessel'
  | 'handleUpdateEntry'
  | 'handleModalSwap'
  | 'onSwapPortCall'
  | 'handleCreateEvent'
  | 'handleDelete'
  | 'onOptimizeVoyageSpeed'
  | 'spotAssignments'
  | 'onVesselChange'
  | 'coaAssignments'
  | 'eventTimelines'
  | 'timelineFocus'
  | 'onUnassignOrder'
  | 'scenario'
  | 'onSpotContractVesselChange'
  | 'navTarget'
  | 'saveAllocations'
  | 'scheduleContext'
  | 'pnlData'
  | 'createAndUpdateAssignments'
  | 'completedData'
  | 'completedOrderIds'
>;
type EditOffhireProps = Pick<
  OffHireModalProps,
  'vesselId' | 'offHire' | 'initialStartTime' | 'onSave' | 'scheduleContext'
>;
type EditOpenPositionProps = Pick<
  OpenPositionModalProps,
  | 'vesselId'
  | 'openPosition'
  | 'initialStartTime'
  | 'scheduleContext'
  | 'lastItemEnd'
  | 'lastPortId'
>;
type VesselReservationProps = Pick<
  VesselReservationModalProps,
  | 'lastItemEnd'
  | 'scheduleContext'
  | 'vesselId'
  | 'vesselName'
  | 'vesselReservation'
>;

type VoyageAttributeProps = Pick<
  VoyageAttributeModalProps,
  | 'clientId'
  | 'shipment'
  | 'hasChange'
  | 'voyageAttributeById'
  | 'patchShipment'
>;

type EditSingleEventProps = Pick<
  SingleEventModalProps,
  | 'vesselId'
  | 'singleEvent'
  | 'initialStartTime'
  | 'handleCreateEvent'
  | 'handleUpdateEntry'
  | 'scheduleContext'
  | 'timeline'
>;
type CreateCoaProps = Pick<NewCoaModalProps, 'onCreate' | 'coaIdToDuplicate'>;
type EditCoaProps = Pick<EditCoaModalProps, 'onEdit' | 'coaId'>;
type EditSpotProps = Pick<EditSpotModalProps, 'onEdit' | 'spotId'>;
type CommitScenarioProps = Pick<
  CommitScenarioModalProps,
  | 'onCommit'
  | 'scenarioToCommit'
  | 'masterScenario'
  | 'timelines'
  | 'committedData'
  | 'getScenarioData'
>;
type SelectCompletedProps = Pick<
  SelectCompletedModalProps,
  'onSelectCompletedBeforeDate'
>;
type VoyageCharterProps = Pick<
  VoyageCharterModalProps,
  | 'voyageCharterId'
  | 'duplicate'
  | 'handleAddToAssignment'
  | 'handleGetSuggestionsForOrders'
  | 'reApplyCargoAllocations'
>;

type BatchEditCargoesProps = Pick<BatchEditCargoesModalProps, 'orders'>;

interface ActiveModalMapping {
  CREATE_ORDER: CreateOrderProps;
  VIEW_ORDER: ViewOrderProps;
  VIEW_SHIPMENT: ViewShipmentProps;
  EDIT_OFFHIRE: EditOffhireProps;
  EDIT_OPEN_POSITION: EditOpenPositionProps;
  EDIT_SINGLE_EVENT: EditSingleEventProps;
  CREATE_COA: CreateCoaProps;
  EDIT_COA: EditCoaProps;
  EDIT_SPOT: EditSpotProps;
  COMMIT_SCENARIO: CommitScenarioProps;
  SELECT_COMPLETED: SelectCompletedProps;
  EDIT_VOYAGE_CHARTER: VoyageCharterProps;
  VESSEL_RESERVATION: VesselReservationProps;
  VOYAGE_ATTRIBUTE: VoyageAttributeProps;
  BATCH_EDIT_CARGOES: BatchEditCargoesProps;
  SUGGESTION_ESTIMATOR: SuggestionEstimatorProps;
}

export type ActiveModal = {
  [K in keyof ActiveModalMapping]: { type: K; props: ActiveModalMapping[K] };
}[keyof ActiveModalMapping];

export type ActiveModalWithKey = ActiveModal & { key: string };

type ModalContextProps = {
  activeModals: ActiveModalWithKey[];
  openModal: (modal: ActiveModal) => void;
};

export const ModalContext = React.createContext<ModalContextProps>({
  activeModals: [],
  openModal: () => null,
});

export const useModal = (): {
  activeModals: ActiveModalWithKey[];
  openModal: (modal: ActiveModal) => void;
} => {
  const { activeModals, openModal } = useContext(ModalContext);

  return { activeModals, openModal };
};

type ModalProviderHooks = {
  activeModals: ActiveModalWithKey[];
  visibleModal: ActiveModalWithKey | null;
  closeAllModals: () => void;
  openModal: (modal: ActiveModal) => void;
  closeModal: (modal: ActiveModal) => void;
};

export function useModalProvider(): ModalProviderHooks {
  const [activeModals, setActiveModals] = useState<ActiveModalWithKey[]>([]);

  const openModal: ModalContextProps['openModal'] = useCallback((modal) => {
    setActiveModals((prevState) => {
      return [
        ...prevState,
        {
          ...modal,
          key: new Date().toISOString(),
        },
      ];
    });
  }, []);

  const closeModal = useCallback((modal: ActiveModal) => {
    setActiveModals((activeModals) =>
      activeModals.filter((activeModal) => activeModal !== modal),
    );
  }, []);

  const closeAllModals = useCallback(() => {
    setActiveModals([]);
  }, []);

  // Only last opened modal is visible
  const visibleModal = useMemo(
    () =>
      activeModals.length > 0 ? activeModals[activeModals.length - 1] : null,
    [activeModals],
  );

  return useMemo(
    () => ({
      activeModals,
      openModal,
      closeModal,
      closeAllModals,
      visibleModal,
    }),
    [activeModals, openModal, closeModal, closeAllModals, visibleModal],
  );
}

type PropsByModalType = ActiveModalMapping;

type ModalProviderProps = ModalProviderHooks & {
  children: React.ReactNode;
  timeData?: TimeData[][];
  tcTimeDataByVessel?: TcTimeDataByVessel;
  eventTimelines?: EventTimeline[];
  timelineFocus?: TimelineFocus;
  setSelectedOrderIds?: (orderIds: Array<number>) => void;
  onSaveAndCloseOrder?: (args: {
    orderIds: number[];
    contractType?: ContractDetailsSchema['type'];
    isPreliminary?: boolean;
  }) => void;
  relatedOrgsData?: RelatedOrgsQuery | undefined;
} & Partial<PropsByModalType>;

export const ModalProvider = (props: ModalProviderProps): JSX.Element => {
  const {
    children,
    activeModals,
    openModal,
    closeModal,
    closeAllModals,
    visibleModal,
    eventTimelines,
    timelineFocus,
    tcTimeDataByVessel,
    onSaveAndCloseOrder,
    setSelectedOrderIds,
    relatedOrgsData,
  } = props;

  return (
    <ModalContext.Provider value={{ activeModals, openModal }}>
      {children}
      {activeModals.map((activeModal, index) => {
        const ModalComponent = modals[activeModal.type].component;
        const previousLabel =
          index > 0 ? modals[activeModals[index - 1].type].label : undefined;
        const isVisible = activeModal === visibleModal;
        const onPrevious =
          index > 0 ? () => closeModal(activeModal) : closeAllModals;
        const modalProps = 'props' in activeModal ? activeModal.props : {};
        const { key } = activeModal;

        return (
          <ModalComponent
            {...modalProps}
            isOpen={true}
            key={key}
            modalKey={key}
            onClose={closeAllModals}
            onPrevious={onPrevious}
            previousLabel={previousLabel}
            isVisible={isVisible}
            tcTimeDataByVessel={tcTimeDataByVessel}
            eventTimelines={eventTimelines}
            timelineFocus={timelineFocus}
            onSaveAndCloseOrder={onSaveAndCloseOrder}
            setSelectedOrderIds={setSelectedOrderIds}
            relatedOrgsData={relatedOrgsData}
            {...props[activeModal.type]}
          />
        );
      })}
    </ModalContext.Provider>
  );
};
